<script setup>
/**
 * Онлайн-запись на консультацию со страницы оффера.
 * Шаги: время → формат (онлайн / офис) → тип (бесплатно / разбор документов) → телефон → итог.
 *
 * ⚠ СВОБОДНЫЕ ЧАСЫ ПОКА СЛУЧАЙНЫЕ. Синхронизации с расписанием юриста нет (решение
 * владельца 25.10.2026): занятость генерируется из даты, стабильно для одного дня.
 * Поэтому время — пожелание клиента, менеджер подтверждает его звонком (так и
 * написано в лиде CRM). Когда появится расписание — заменить busyFor() данными.
 *
 * ⚠ ВЫСОТА ТЕЛА ШАГОВ ФИКСИРОВАНА (min-h): шаги разной длины, и без этого блок
 * прыгал бы, сдвигая всё, что ниже, — особенно заметно на телефоне.
 *
 * Отправка: POST /api/booking-lead (QuizController::booking) → лид в CRM + письмо.
 * Запись запоминается в cookie nd_booking_<оффер> до конца дня консультации: после
 * перезагрузки вместо формы сразу итог «Вы записаны», заново заполнять не нужно.
 * Cookie читаем в onMounted — при SSR сервер рисует форму, иначе гидрация разойдётся.
 *
 * Цели Метрики: CONSULT_START (выбрал час — начал запись), CONSULT_BOOK (записался),
 * CONSULT_CONTACT (просит связаться заранее).
 */
import { ref, computed, onMounted } from "vue";

const props = defineProps({
  uslugaId: { type: Number, required: true },
  lawyerName: { type: String, default: "" },
  address: { type: String, default: "" },
});

const DAYS_AHEAD = 14;
const HOURS = Array.from({ length: 12 }, (_, i) => 9 + i); // 9:00 … 20:00
const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const MONTHS_GEN = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const WEEKDAYS = ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"];

const now = new Date();
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
const lastDay = new Date(today);
lastDay.setDate(lastDay.getDate() + DAYS_AHEAD);

const keyOf = (d) => d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
const dateOfKey = (k) => new Date(Math.floor(k / 10000), (Math.floor(k / 100) % 100) - 1, k % 100);
const isoOfKey = (k) => `${Math.floor(k / 10000)}-${String(Math.floor(k / 100) % 100).padStart(2, "0")}-${String(k % 100).padStart(2, "0")}`;
const todayKey = keyOf(today);
const inWindow = (d) => d >= today && d <= lastDay;

// Занятость: случайная, но стабильная для дня (генератор с зерном от даты).
// Сегодняшние часы, которые уже наступили, тоже заняты.
function busyFor(key) {
  let seed = key % 233280;
  const rnd = () => ((seed = (seed * 9301 + 49297) % 233280), seed / 233280);
  rnd(); rnd();
  const busy = {};
  HOURS.forEach((h) => { busy[h] = rnd() < 0.42 || (key === todayKey && h <= now.getHours()); });
  return busy;
}

// По умолчанию — сегодня, а если свободных часов уже нет — ближайший день со свободными
function firstFreeKey() {
  for (let i = 0; i <= DAYS_AHEAD; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const b = busyFor(keyOf(d));
    if (HOURS.some((h) => !b[h])) return keyOf(d);
  }
  return todayKey;
}

const STEPS = ["time", "format", "kind", "phone"];
const STEP_NAMES = ["Время", "Формат", "Консультация", "Телефон"];

const step = ref("time");
const dayKey = ref(firstFreeKey());
const hour = ref(null);
const format = ref(null);
const kind = ref(null);
const digits = ref("");
const phoneTouched = ref(false);
const tried = ref(false);
const agree = ref(false);
const sending = ref(false);
const sendError = ref("");
const channels = ref([]);
const finished = ref(false);
const viewMonth = ref(today.getFullYear() * 12 + today.getMonth());

const stepIndex = computed(() => (step.value === "done" ? STEPS.length : STEPS.indexOf(step.value)));
const stepDone = computed(() => ({ time: hour.value !== null, format: !!format.value, kind: !!kind.value, phone: false }));

// ── календарь ──
const selDate = computed(() => dateOfKey(dayKey.value));
const busy = computed(() => busyFor(dayKey.value));
const freeCount = computed(() => HOURS.filter((h) => !busy.value[h]).length);
const dayName = computed(() => (dayKey.value === todayKey ? "Сегодня" : WEEKDAYS[selDate.value.getDay()]));
const dayTitle = computed(() => `${dayName.value}, ${selDate.value.getDate()} ${MONTHS_GEN[selDate.value.getMonth()]}`);
const rangeText = `с ${today.getDate()} ${MONTHS_GEN[today.getMonth()]} по ${lastDay.getDate()} ${MONTHS_GEN[lastDay.getMonth()]}`;

// ПК: месяц с листанием в пределах окна записи
const minView = today.getFullYear() * 12 + today.getMonth();
const maxView = lastDay.getFullYear() * 12 + lastDay.getMonth();
const monthTitle = computed(() => `${MONTHS[viewMonth.value % 12]} ${Math.floor(viewMonth.value / 12)}`);
const monthCells = computed(() => {
  const y = Math.floor(viewMonth.value / 12), m = viewMonth.value % 12;
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const cells = Array.from({ length: lead }, (_, i) => ({ id: "e" + i, empty: true }));
  const days = new Date(y, m + 1, 0).getDate();
  for (let d = 1; d <= days; d++) {
    const dt = new Date(y, m, d);
    cells.push({ id: keyOf(dt), key: keyOf(dt), n: d, open: inWindow(dt), today: keyOf(dt) === todayKey });
  }
  return cells;
});

// Телефон: две недели строками по 7 дней — ровно окно записи, без пустого месяца
const weekCells = computed(() => {
  const start = new Date(today);
  start.setDate(start.getDate() - ((today.getDay() + 6) % 7));
  const end = new Date(lastDay);
  end.setDate(end.getDate() + (6 - ((lastDay.getDay() + 6) % 7)));
  const cells = [];
  for (const dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
    const d = new Date(dt);
    const k = keyOf(d);
    const sub = k === todayKey ? "сегодня" : d.getDate() === 1 || k === keyOf(start) ? MONTHS_SHORT[d.getMonth()] : "";
    cells.push({ id: k, key: k, n: d.getDate(), open: inWindow(d), sub, label: `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}` });
  }
  return cells;
});
const weekTitle = computed(() =>
  today.getMonth() === lastDay.getMonth()
    ? `${MONTHS[today.getMonth()]} ${today.getFullYear()}`
    : `${MONTHS[today.getMonth()]} — ${MONTHS[lastDay.getMonth()].toLowerCase()} ${lastDay.getFullYear()}`
);

function pickDay(k) {
  dayKey.value = k;
  hour.value = null;
}
let started = false;
function pickHour(h) {
  if (busy.value[h]) return;
  if (!started) { started = true; window.goal?.("CONSULT_START"); } // начал запись — верх воронки
  hour.value = h;
  step.value = "format"; // выбор часа сразу ведёт дальше; «Далее» дублирует для вернувшихся
}

// ── итог выбора ──
const timeText = computed(() => `${dayName.value.toLowerCase()}, ${selDate.value.getDate()} ${MONTHS_GEN[selDate.value.getMonth()]}${hour.value !== null ? ", " + hour.value + ":00" : ""}`);
const formatText = computed(() => (format.value === "office" ? "в офисе" : "онлайн"));
const kindText = computed(() => (kind.value === "paid" ? "разбор документов" : "бесплатно, 20 минут"));
const chips = computed(() => {
  const out = [];
  if (stepIndex.value >= 1 && step.value !== "done") out.push({ text: timeText.value, to: "time" });
  if (stepIndex.value >= 2 && step.value !== "done") out.push({ text: formatText.value, to: "format" });
  if (stepIndex.value >= 3 && step.value !== "done") out.push({ text: kindText.value, to: "kind" });
  return out;
});
const subtitle = computed(() => ({ time: "Выберите день и свободное время", format: "Шаг 2 из 4", kind: "Шаг 3 из 4", phone: "Последний шаг — куда позвонить", done: "" })[step.value]);

function pickFormat(v) { format.value = v; step.value = "kind"; }
function pickKind(v) { kind.value = v; step.value = "phone"; }
function goBack() { if (stepIndex.value > 0) step.value = STEPS[stepIndex.value - 1]; }
function goNext() { if (stepDone.value[step.value] && stepIndex.value < STEPS.length - 1) step.value = STEPS[stepIndex.value + 1]; }

// ── телефон: «+7» всегда, дальше 10 цифр; ведущие 8/7 при вставке отбрасываются ──
const focused = ref(false);
function formatPhone(d) {
  if (!d) return "+7 ";
  let out = "+7 (" + d.slice(0, 3);
  if (d.length >= 3) out += ")";
  if (d.length > 3) out += " " + d.slice(3, 6);
  if (d.length > 6) out += "-" + d.slice(6, 8);
  if (d.length > 8) out += "-" + d.slice(8, 10);
  return out;
}
const phoneValue = computed(() => (digits.value || phoneTouched.value || focused.value ? formatPhone(digits.value) : ""));
function onPhoneInput(e) {
  const raw = e.target.value;
  let d = raw.replace(/^\+7/, "").replace(/\D/g, "");
  if (d.length >= 11 && (d[0] === "7" || d[0] === "8")) d = d.slice(1);
  d = d.slice(0, 10);
  // Стёрли скобку или дефис без цифры — убираем последнюю цифру, иначе маска её вернёт
  if (raw.length < formatPhone(digits.value).length && d === digits.value) d = d.slice(0, -1);
  digits.value = d;
  e.target.value = formatPhone(d);
}
const phoneOk = computed(() => digits.value.length === 10);
const phoneErr = computed(() => (phoneTouched.value || tried.value) && !phoneOk.value);
const phoneErrText = computed(() => (digits.value.length === 0 ? "Введите номер телефона" : `Номер неполный: нужно 10 цифр после +7, сейчас ${digits.value.length}`));
const agreeErr = computed(() => tried.value && !agree.value);

function ymUid() {
  const m = document.cookie.match(/(?:^|;\s*)_ym_uid=(\d+)/);
  return m ? m[1] : "";
}
async function post(payload) {
  const r = await fetch("/api/booking-lead", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ usluga_id: props.uslugaId, phone: digits.value, page_url: window.location.href, ym_uid: ymUid(), ...payload }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || !data.success) throw new Error(data.message || "Не удалось записать");
  return data;
}

// ── память о записи (cookie до конца дня консультации) ──
const COOKIE = `nd_booking_${props.uslugaId}`;
function saveBooking() {
  const data = { day: dayKey.value, hour: hour.value, format: format.value, kind: kind.value, finished: finished.value, channels: channels.value };
  const expires = dateOfKey(dayKey.value);
  expires.setHours(23, 59, 59);
  document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}
function clearBooking() {
  document.cookie = `${COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}
onMounted(() => {
  const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + COOKIE + "=([^;]+)"));
  if (!m) return;
  try {
    const d = JSON.parse(decodeURIComponent(m[1]));
    if (!d.day || d.day < todayKey || d.hour == null) return clearBooking(); // консультация уже прошла
    dayKey.value = d.day; hour.value = d.hour; format.value = d.format; kind.value = d.kind;
    finished.value = !!d.finished; channels.value = Array.isArray(d.channels) ? d.channels : [];
    step.value = "done";
  } catch (e) {
    clearBooking();
  }
});

async function confirm() {
  tried.value = true;
  phoneTouched.value = true;
  if (!phoneOk.value || !agree.value || sending.value) return;
  sending.value = true;
  sendError.value = "";
  try {
    await post({ stage: "book", date: isoOfKey(dayKey.value), hour: hour.value, format: format.value, kind: kind.value, agree: true });
    window.goal?.("CONSULT_BOOK");
    step.value = "done";
    saveBooking();
  } catch (e) {
    sendError.value = "Не получилось записать. Позвоните нам — запишем по телефону.";
  } finally {
    sending.value = false;
  }
}

async function finish() {
  finished.value = true;
  saveBooking();
  if (!channels.value.length) return;
  window.goal?.("CONSULT_CONTACT");
  try { await post({ stage: "contact", channels: channels.value }); } catch (e) { /* запись уже есть — не пугаем */ }
}
const finishText = computed(() => {
  const map = { max: "напишем в MAX", tg: "напишем в Telegram", call: "позвоним на ваш номер" };
  const ch = channels.value.map((c) => map[c]);
  return ch.length ? `До консультации свяжемся с вами: ${ch.join(", ")}. Ждём вас в назначенное время.` : "Ждём вас в назначенное время. Напоминание придёт за час.";
});
const doneText = computed(() =>
  format.value === "office"
    ? `Ждём вас в офисе${props.address ? ": " + props.address : ""}. Перед встречей перезвоним и подтвердим время.`
    : `${props.lawyerName ? props.lawyerName.split(" ")[0] : "Юрист"} позвонит в это время. Перед консультацией подтвердим запись.`
);

function reset() {
  clearBooking();
  step.value = "time";
  hour.value = null; format.value = null; kind.value = null;
  agree.value = false; tried.value = false; phoneTouched.value = false;
  channels.value = []; finished.value = false; sendError.value = "";
}

// ── классы ──
const dayBtn = (c, sel) =>
  !c.open ? "text-gray-300 cursor-default"
  : sel ? "bg-indigo-600 text-white font-extrabold border border-indigo-600"
  : "bg-white text-indigo-800 font-bold border border-indigo-200 hover:border-indigo-400";
const hourBtn = (h) =>
  busy.value[h] ? "bg-gray-100 text-gray-400 border border-gray-200 line-through cursor-not-allowed"
  : hour.value === h ? "bg-indigo-600 text-white font-extrabold border border-indigo-600"
  : "bg-white text-indigo-800 font-bold border border-indigo-200 hover:border-indigo-400";
const card = (on) => (on ? "border-2 border-indigo-600 bg-white" : "border border-gray-300 bg-white hover:border-indigo-400");
</script>

<template>
  <section id="booking" class="scroll-mt-24 bg-indigo-50 px-4 py-6 md:px-8">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <h2 class="flex items-center gap-2 text-2xl font-bold tracking-tight text-gray-900">
          <svg class="h-6 w-6 shrink-0 text-indigo-600" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></svg>
          Записаться на консультацию
        </h2>
        <p v-if="subtitle" class="mt-1 text-sm text-gray-600">{{ subtitle }}</p>
      </div>
      <ol class="m-0 grid list-none grid-cols-4 gap-2 p-0 lg:w-[460px]" aria-label="Шаги записи">
        <li v-for="(name, i) in STEP_NAMES" :key="name" class="flex flex-col gap-1">
          <span class="h-1 rounded-full" :class="i <= stepIndex ? 'bg-indigo-600' : 'bg-indigo-100'" aria-hidden="true"></span>
          <span class="text-xs font-bold" :class="i === stepIndex ? 'text-gray-900' : i < stepIndex ? 'text-indigo-600' : 'text-gray-400'" :aria-current="i === stepIndex ? 'step' : undefined">{{ name }}</span>
        </li>
      </ol>
    </div>

    <div class="mt-4 rounded-xl border border-indigo-100 bg-white p-4 shadow-sm md:p-6">
      <!-- ⚠ Фиксированная высота тела шагов (вместе с плашками выбора) — блок не прыгает при переходах -->
      <div class="min-h-[520px] lg:min-h-[440px]">
        <!-- Сделанный выбор: плашки ведут обратно на свой шаг -->
        <div v-if="chips.length" class="mb-4 flex flex-wrap gap-2">
          <button v-for="c in chips" :key="c.to" type="button" class="min-h-[36px] rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-left text-sm font-bold text-indigo-800 hover:border-indigo-400" @click="step = c.to">
            {{ c.text }} · изменить
          </button>
        </div>

        <!-- 1. Время -->
        <div v-if="step === 'time'" class="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-8">
          <!-- ПК: месяц -->
          <div class="hidden flex-col gap-2 lg:flex">
            <div class="flex items-center justify-between">
              <span class="text-base font-extrabold text-gray-900">{{ monthTitle }}</span>
              <div class="flex gap-1">
                <button type="button" aria-label="Прошлый месяц" :disabled="viewMonth <= minView" class="h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300" @click="viewMonth--">‹</button>
                <button type="button" aria-label="Следующий месяц" :disabled="viewMonth >= maxView" class="h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300" @click="viewMonth++">›</button>
              </div>
            </div>
            <div class="grid grid-cols-7 gap-1 text-center text-xs font-bold text-gray-500">
              <span v-for="w in ['Пн','Вт','Ср','Чт','Пт','Сб','Вс']" :key="w">{{ w }}</span>
            </div>
            <div class="grid grid-cols-7 gap-1">
              <template v-for="c in monthCells" :key="c.id">
                <span v-if="c.empty"></span>
                <button v-else type="button" :disabled="!c.open" class="h-10 rounded-lg text-sm" :class="[dayBtn(c, c.key === dayKey), c.today && c.key !== dayKey ? 'ring-2 ring-inset ring-indigo-600' : '']" :aria-pressed="c.key === dayKey" @click="pickDay(c.key)">{{ c.n }}</button>
              </template>
            </div>
            <span class="text-xs text-gray-500">Запись открыта на две недели: {{ rangeText }}</span>
          </div>

          <!-- Телефон: две недели -->
          <div class="flex flex-col gap-2 lg:hidden">
            <div class="flex items-baseline justify-between">
              <span class="text-base font-extrabold text-gray-900">{{ weekTitle }}</span>
              <span class="text-xs text-gray-500">запись на 2 недели</span>
            </div>
            <div class="grid grid-cols-7 gap-1 text-center text-xs font-bold text-gray-500">
              <span v-for="w in ['Пн','Вт','Ср','Чт','Пт','Сб','Вс']" :key="w">{{ w }}</span>
            </div>
            <div class="grid grid-cols-7 gap-1">
              <button v-for="c in weekCells" :key="c.id" type="button" :disabled="!c.open" class="flex h-12 flex-col items-center justify-center rounded-lg leading-tight" :class="dayBtn(c, c.key === dayKey)" :aria-label="c.label + (c.open ? '' : ', запись недоступна')" :aria-pressed="c.key === dayKey" @click="pickDay(c.key)">
                <span class="text-base">{{ c.n }}</span>
                <span v-if="c.sub" class="text-[10px] font-semibold" :class="c.key === dayKey ? 'text-indigo-100' : c.open ? 'text-indigo-500' : 'text-gray-300'">{{ c.sub }}</span>
              </button>
            </div>
          </div>

          <!-- Часы -->
          <div class="flex flex-col gap-3">
            <div class="flex items-baseline justify-between">
              <span class="text-base font-extrabold text-gray-900">{{ dayTitle }}</span>
              <span class="text-xs text-gray-500">свободно {{ freeCount }} из {{ HOURS.length }}</span>
            </div>
            <div class="grid grid-cols-4 gap-2">
              <button v-for="h in HOURS" :key="h" type="button" :disabled="busy[h]" class="h-11 rounded-lg text-base" :class="hourBtn(h)" :aria-label="`${h}:00, ${busy[h] ? 'занято' : 'свободно'}`" :aria-pressed="hour === h" @click="pickHour(h)">{{ h }}:00</button>
            </div>
            <div class="flex gap-4 text-xs text-gray-500">
              <span class="flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-sm border border-indigo-200 bg-white"></span>свободно</span>
              <span class="flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-sm border border-gray-200 bg-gray-100"></span>занято</span>
            </div>
          </div>
        </div>

        <!-- 2. Формат -->
        <div v-else-if="step === 'format'" class="flex flex-col gap-3">
          <span class="text-lg font-extrabold text-gray-900">Как вам удобнее провести консультацию?</span>
          <div class="grid gap-3 lg:max-w-3xl lg:grid-cols-2">
            <button type="button" class="flex flex-col gap-1 rounded-xl p-4 text-left" :class="card(format === 'online')" :aria-pressed="format === 'online'" @click="pickFormat('online')">
              <span class="text-base font-extrabold text-gray-900">Онлайн</span>
              <span class="text-sm text-gray-600">По телефону или видеосвязи. Можно из любого города.</span>
            </button>
            <button type="button" class="flex flex-col gap-1 rounded-xl p-4 text-left" :class="card(format === 'office')" :aria-pressed="format === 'office'" @click="pickFormat('office')">
              <span class="text-base font-extrabold text-gray-900">Приду в офис</span>
              <span class="text-sm text-gray-600">{{ address ? address + '. ' : '' }}Возьмите с собой документы.</span>
            </button>
          </div>
        </div>

        <!-- 3. Тип консультации -->
        <div v-else-if="step === 'kind'" class="flex flex-col gap-3">
          <span class="text-lg font-extrabold text-gray-900">Что вам нужно от консультации?</span>
          <div class="grid gap-3 lg:max-w-3xl lg:grid-cols-2">
            <button type="button" class="flex flex-col gap-1.5 rounded-xl p-4 text-left" :class="card(kind === 'free')" :aria-pressed="kind === 'free'" @click="pickKind('free')">
              <span class="text-base font-extrabold text-gray-900">Понять, есть ли перспектива</span>
              <span class="text-sm text-gray-600">Юрист выслушает ситуацию и скажет, стоит ли идти дальше и что делать в первую очередь.</span>
              <span class="text-sm font-extrabold text-emerald-700">Бесплатно · 20 минут</span>
            </button>
            <button type="button" class="flex flex-col gap-1.5 rounded-xl p-4 text-left" :class="card(kind === 'paid')" :aria-pressed="kind === 'paid'" @click="pickKind('paid')">
              <span class="text-base font-extrabold text-gray-900">Разобрать документы и получить план действий</span>
              <span class="text-sm text-gray-600">Юрист заранее изучит документы, оценит риски и распишет шаги: куда идти, что подавать, сколько это займёт.</span>
              <span class="text-sm font-extrabold text-gray-900">Стоимость назовём при подтверждении · до 60 минут</span>
            </button>
          </div>
        </div>

        <!-- 4. Телефон -->
        <div v-else-if="step === 'phone'" class="flex max-w-md flex-col gap-2.5">
          <label for="booking-phone" class="text-base font-bold text-gray-900">Ваш телефон</label>
          <input
            id="booking-phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="+7 (___) ___-__-__"
            :value="phoneValue" :aria-invalid="phoneErr" aria-describedby="booking-phone-err"
            class="h-12 rounded-lg px-3 text-lg tracking-wide focus:border-indigo-500 focus:ring-indigo-500"
            :class="phoneErr ? 'border-2 border-rose-600 bg-rose-50' : 'border-gray-300'"
            @input="onPhoneInput" @focus="focused = true" @blur="focused = false; phoneTouched = true"
          />
          <span v-if="phoneErr" id="booking-phone-err" role="alert" class="text-sm font-semibold text-rose-600">{{ phoneErrText }}</span>
          <label class="flex cursor-pointer items-start gap-2.5 py-1 text-sm text-gray-700">
            <input v-model="agree" type="checkbox" :aria-invalid="agreeErr" class="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <span>Согласен с <a href="/policy" target="_blank" class="text-indigo-600 hover:underline">политикой обработки персональных данных</a></span>
          </label>
          <span v-if="agreeErr" role="alert" class="text-sm font-semibold text-rose-600">Отметьте согласие — без него мы не можем записать вас.</span>
          <button type="button" :disabled="sending" class="mt-1 h-12 rounded-lg bg-indigo-600 text-base font-extrabold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-60" @click="confirm">
            {{ sending ? "Записываем…" : "Записаться" }}
          </button>
          <span v-if="sendError" role="alert" class="text-sm font-semibold text-rose-600">{{ sendError }}</span>
        </div>

        <!-- Итог -->
        <div v-else class="flex max-w-xl flex-col gap-3">
          <div class="flex flex-col gap-1 rounded-xl bg-emerald-50 p-4">
            <span class="text-lg font-extrabold text-emerald-700">Вы записаны: {{ timeText }} · {{ formatText }} · {{ kindText }}</span>
            <span class="text-sm text-emerald-800">{{ doneText }}</span>
          </div>
          <div v-if="!finished" class="flex flex-col gap-2 rounded-xl border border-gray-200 p-4">
            <span class="text-base font-extrabold text-gray-900">Хотите связаться заранее? <span class="font-semibold text-gray-500">— необязательно</span></span>
            <span class="text-sm text-gray-600">Отметьте, как вам удобно. В мессенджер напишем, если он привязан к вашему номеру. Можно сразу прислать документы.</span>
            <label v-for="o in [{ v: 'max', t: 'Написать в MAX' }, { v: 'tg', t: 'Написать в Telegram' }, { v: 'call', t: 'Позвонить на мой номер' }]" :key="o.v" class="flex min-h-[40px] cursor-pointer items-center gap-2.5 text-base font-bold text-gray-900">
              <input v-model="channels" type="checkbox" :value="o.v" class="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />{{ o.t }}
            </label>
            <span v-if="channels.length" class="text-sm font-bold text-emerald-700">Свяжемся с вами в ближайшее время.</span>
            <button type="button" class="mt-1 h-11 self-start rounded-lg bg-indigo-600 px-7 text-base font-extrabold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2" @click="finish">Завершить</button>
          </div>
          <div v-else class="flex flex-col gap-1 rounded-xl border border-gray-200 p-4">
            <span class="text-base font-extrabold text-gray-900">Спасибо, всё готово</span>
            <span class="text-sm text-gray-600">{{ finishText }}</span>
          </div>
          <button type="button" class="self-start py-2 text-sm font-bold text-indigo-600 hover:underline" @click="reset">Записаться на другое время</button>
        </div>
      </div>

      <!-- Назад / Далее -->
      <div v-if="step !== 'done'" class="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
        <button type="button" :disabled="stepIndex === 0" class="h-11 rounded-lg border border-gray-300 bg-white px-5 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300" @click="goBack">‹ Назад</button>
        <button v-if="step !== 'phone'" type="button" :disabled="!stepDone[step]" class="h-11 rounded-lg bg-indigo-600 px-6 text-sm font-extrabold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400" @click="goNext">Далее ›</button>
      </div>
    </div>
  </section>
</template>
